import type { FC, ReactNode, ButtonHTMLAttributes } from "react";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "gradient" | "danger";
  size?: "sm" | "md" | "lg";
  loading?: boolean;
  fullWidth?: boolean;
}

export const Button: FC<ButtonProps> = ({
  children,
  variant = "primary",
  size = "md",
  loading = false,
  fullWidth = false,
  className = "",
  disabled,
  ...props
}) => {
  const baseStyles = `
    relative inline-flex items-center justify-center gap-2
    font-medium rounded-lg border cursor-pointer
    transition-all duration-200
    focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--color-bg)]
  `;

  const variants = {
    primary: `
      bg-[var(--color-text)] text-[var(--color-bg)] border-[var(--color-text)]
      hover:bg-[rgba(255,255,255,0.9)] hover:border-[rgba(139,92,246,0.6)]
    `,
    secondary: `
      bg-transparent text-[var(--color-text)] border-[var(--color-border)]
      hover:border-[rgba(139,92,246,0.5)] hover:bg-[rgba(139,92,246,0.05)]
    `,
    ghost: `
      bg-transparent text-[var(--color-text-muted)] border-transparent
      hover:text-[var(--color-text)] hover:border-[rgba(139,92,246,0.5)] hover:bg-[rgba(139,92,246,0.05)]
    `,
    gradient: `
      btn-gradient-border border-none text-[var(--color-text)]
      hover:text-white
    `,
    danger: `
      bg-[var(--color-error)] text-white border-[var(--color-error)]
      hover:bg-[var(--color-error)]/80 hover:border-[var(--color-error)]/80
    `,
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-5 py-2.5 text-sm",
    lg: "px-6 py-3 text-base",
  };

  const disabledStyles = "opacity-50 cursor-not-allowed";

  return (
    <button
      className={`
        ${baseStyles}
        ${variants[variant]}
        ${sizes[size]}
        ${fullWidth ? "w-full" : ""}
        ${disabled || loading ? disabledStyles : ""}
        ${className}
      `}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center bg-inherit rounded-lg">
          <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" opacity="0.25" />
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round" />
          </svg>
        </span>
      )}
      <span className={loading ? "invisible" : "flex items-center gap-2"}>{children}</span>
    </button>
  );
};
